import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, CheckCircle2, Circle, ArrowRight, Sparkles, FileSignature, Camera, Wind, UserPlus } from "lucide-react";

const STEPS = [
  { key: "consent", icon: FileSignature, to: "/app/coach/consent-forms" },
  { key: "story", icon: Camera, to: "/app/coach/content" },
  { key: "program", icon: Wind, to: "/app/coach/plans" },
  { key: "invite", icon: UserPlus, to: "/app/coach" },
];

export default function CoachSetup() {
  const { user } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  usePageMeta(t("cs.meta_title"));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      // Auto-detect each step from existing artefacts, then read final state.
      await Promise.all(STEPS.map((s) => api.post(`/coach/setup-progress/auto-detect/${s.key}`).catch(() => null)));
      const { data: d } = await api.get("/coach/setup-progress");
      setData(d);
    } catch { setData({ steps: {}, done: 0, total: 4, complete: false, founding_badge: { active: false } }); }
  }, []);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const markDone = async (key) => {
    setBusy(key);
    try { const { data: d } = await api.post(`/coach/setup-progress/mark-done/${key}`); setData(d); }
    catch { /* noop */ }
    finally { setBusy(null); }
  };

  const steps = data?.steps || {};
  const done = data?.done ?? 0;
  const total = data?.total ?? STEPS.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const badge = data?.founding_badge || {};

  return (
    <div data-testid="coach-setup-page">
      <Seo title={t("cs.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="cs-back">
        <ChevronLeft className="h-4 w-4" /> {t("cs.back")}
      </Link>
      <PageHeader eyebrow={t("cs.eyebrow")} title={t("cs.title")} subtitle={t("cs.subtitle")} />

      {data === null ? (
        <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : (
        <div className="space-y-5">
          {/* Progress */}
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="cs-progress">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground">{t("cs.progress_label", { done, total })}</p>
              <span className="font-display text-xl font-semibold text-primary">{pct}%</span>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-border">
              <div className="h-2 rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>

          {/* Founding badge */}
          {badge.active && (
            <div className="flex items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-5" data-testid="cs-founding-badge">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700"><Sparkles className="h-6 w-6" /></div>
              <div className="min-w-0">
                <p className="font-display text-lg text-amber-900">{t("cs.badge_title")}</p>
                <p className="text-sm text-amber-800">{t("cs.badge_sub", { n: badge.days_left ?? 30 })}</p>
              </div>
            </div>
          )}

          {/* Steps */}
          <div className="space-y-2">
            {STEPS.map((s, i) => {
              const isDone = Boolean(steps[s.key]);
              const Icon = s.icon;
              return (
                <div key={s.key} data-testid={`cs-step-${s.key}`} className={`rounded-2xl border p-4 ${isDone ? "border-emerald-200 bg-emerald-50/50" : "border-border bg-card"}`}>
                  <div className="flex items-start gap-3">
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${isDone ? "bg-emerald-100 text-emerald-600" : "bg-primary/10 text-primary"}`}>
                      {isDone ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-muted-foreground">{t("cs.step_n", { n: i + 1 })}</span>
                        {isDone && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> {t("cs.done")}</span>}
                      </div>
                      <p className="mt-0.5 font-semibold text-foreground">{t(`cs.step_${s.key}_title`)}</p>
                      <p className="mt-0.5 text-sm text-muted-foreground">{t(`cs.step_${s.key}_sub`)}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button size="sm" className="gap-1.5 rounded-full" onClick={() => navigate(s.to)} data-testid={`cs-step-go-${s.key}`}>
                          {t(`cs.step_${s.key}_cta`)} <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                        {!isDone && (
                          <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy === s.key} onClick={() => markDone(s.key)} data-testid={`cs-step-mark-${s.key}`}>
                            <Circle className="h-3.5 w-3.5" /> {t("cs.mark_done")}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
